import { useCallback, useEffect, useRef, useState } from 'react';

import { imageDetailModal } from '@/shared/modals/ImageDetailModal';
import type { PdfPreviewDocument } from '@/shared/utils/pdfPreviewAdapter';

const DETAIL_IMAGE_LONG_EDGE = 800;
const DETAIL_IMAGE_CACHE_LIMIT = 5;

const isCancelledError = (error: unknown) =>
  error instanceof DOMException && error.name === 'AbortError'
    ? true
    : typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      error.code === 'cancelled';

export const usePdfPageDetailPreview = (preview: PdfPreviewDocument | null) => {
  const [loadingPage, setLoadingPage] = useState<number | null>(null);
  const cacheRef = useRef(new Map<number, string>());
  const controllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    requestIdRef.current += 1;
    controllerRef.current?.abort();
    controllerRef.current = null;
    cacheRef.current.clear();
    setLoadingPage(null);
  }, [preview]);

  useEffect(
    () => () => {
      requestIdRef.current += 1;
      controllerRef.current?.abort();
      cacheRef.current.clear();
    },
    [],
  );

  const openPageDetail = useCallback(
    async (pageNumber: number, fallbackImageSrc?: string) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      controllerRef.current?.abort();
      controllerRef.current = null;
      setLoadingPage(null);

      const cache = cacheRef.current;
      const cachedImageSrc = cache.get(pageNumber);
      if (cachedImageSrc) {
        cache.delete(pageNumber);
        cache.set(pageNumber, cachedImageSrc);
        imageDetailModal.open(cachedImageSrc);
        return;
      }

      if (!preview) {
        if (fallbackImageSrc) imageDetailModal.open(fallbackImageSrc);
        return;
      }

      const controller = new AbortController();
      controllerRef.current = controller;
      setLoadingPage(pageNumber);

      try {
        const image = await preview.renderPageImage(pageNumber, {
          maxLongEdge: DETAIL_IMAGE_LONG_EDGE,
          signal: controller.signal,
        });
        if (requestIdRef.current !== requestId) return;

        cache.set(pageNumber, image.dataUrl);
        if (cache.size > DETAIL_IMAGE_CACHE_LIMIT) {
          const oldestPageNumber = cache.keys().next().value;
          if (oldestPageNumber !== undefined) cache.delete(oldestPageNumber);
        }
        imageDetailModal.open(image.dataUrl);
      } catch (error) {
        if (
          requestIdRef.current === requestId &&
          !isCancelledError(error) &&
          fallbackImageSrc
        ) {
          imageDetailModal.open(fallbackImageSrc);
        }
      } finally {
        if (requestIdRef.current === requestId) {
          controllerRef.current = null;
          setLoadingPage(null);
        }
      }
    },
    [preview],
  );

  return { loadingPage, openPageDetail };
};
